<template>
  <div class="accordion" :id="'accordion-' + localSubtask.id">
    <div class="accordion-item">
      <h2 class="accordion-header" :id="'heading-' + localSubtask.id">
        <button
          class="accordion-button collapsed"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="'#collapse-' + localSubtask.id"
          aria-expanded="false"
          :aria-controls="'collapse-' + localSubtask.id"
        >
          {{ localSubtask.title }}
          <span v-if="localSubtask.to_evaluate" class="ms-3">
            <div v-if="localSubevaluation && localSubevaluation.subevaluation_successful === true" >
              Task passed: 
              <i :class="'bi bi-check-circle-fill text-success'"></i>
            </div>
            <div v-else-if="localSubevaluation && localSubevaluation.subevaluation_successful === false" >
              Task failed: 
              <i :class="'bi bi-x-circle-fill text-danger'"></i>
            </div>
            <div v-else-if="localSubevaluation" >
              Evaluation is running: 
              <i :class="'bi bi-hourglass-split text-warning'"></i>
            </div>
            <div v-else>
              Task failed: 
              <i :class="'bi bi-x-circle-fill text-danger'"></i>
            </div>
          </span>
        </button>
      </h2>
      <div
        :id="'collapse-' + localSubtask.id"
        class="accordion-collapse collapse"
        :aria-labelledby="'heading-' + localSubtask.id"
        :data-bs-parent="'#accordion-' + localSubtask.id"
        ref="collapseElement"
      >
        <div class="accordion-body">
          <div v-if="localSubevaluation && localSubtask.to_evaluate">
            <p class="card-text">
              <i class="bi bi-calendar-event text-primary me-2"></i>
              <strong>Evaluated for Deadline:</strong>
              <i class="bi bi-info-circle ms-2" v-tooltip="'The most recent submission before the deadline has been evaluated. Below is submission date and time.'"></i>
              {{ formatDate(localDeadline.date) }}
            </p>
            <p class="card-text">
              <i class="bi bi-clock text-primary me-2"></i>
              <strong>Started:</strong> {{ formattedSubevaluationStarted }}</p>
            <p class="card-text">
              <i class="bi bi-clock-fill text-primary me-2"></i>
              <strong>Finished:</strong> {{ formattedSubevaluationEnded }}</p>
            <p class="card-text">
              <i class="bi bi-file-earmark-text text-primary me-2"></i>
              <strong>Evaluated submission:</strong>
            </p>
            <ul v-if="localSubmission && localSubmission.submission_date" class="list-group">
              <li class="list-group-item d-flex justify-content-between align-items-center">
                <span>
                  <strong>Submitted on:</strong> {{ formatedSubmissionDate(localSubmission.submission_date) }}
                </span>
                <span>
                  <button v-if="localSubmission.file_paths" class="btn btn-primary btn-sm" @click="downloadFiles(localSubmission.file_paths, 'submission_files.zip')">
                    <i class="bi bi-download"></i> Download submission
                  </button>
                </span>
              </li>
            </ul>
            <div class="my-3">
              <button class="btn btn-info btn-sm" @click="pushToSubtask">
              <i class="bi bi-eye"></i> View Task
              </button>
            </div>
            <div v-if="logContent && isTeacher">
              <h5>Full log:</h5>
              <p class="card-text p-2 border rounded">{{ logContent }}</p>
            </div>
            <div v-if="resultContent">
              <h5>Log from evaluation:</h5>
              <p class="card-text p-2 border rounded">{{ resultContent }}</p>
            </div>
            <AIReviewPanel
              v-if="isTeacher && localSubmission && localSubmission.id"
              :submission-id="localSubmission.id"
            />
            <div v-if="loading" class="text-center my-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
          <div v-else-if="!localSubtask.to_evaluate">
            <p class="card-text">This task is not meant to be evaluated.</p>
          </div>
          <div v-else>
            <p class="card-text">There was no submission for this task.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EvaluationController from '@/controllers/EvaluationController';
import SubtaskController from '@/controllers/SubtaskController';
import Subtask from '@/models/subtask';
import Submission from '@/models/submission';
import Deadline from '@/models/deadline';
import AIReviewPanel from '@/components/AIReviewPanel.vue';

export default {
  components: { AIReviewPanel },
  props: {
    subtask: {
      type: Object,
      required: true,
    },
    deadline: {
      type: Object,
      required: true,
    },
    subevaluation: {
      type: Object,
      required: false,
    },
    submission: {
      type: Object,
      required: false,
    },
  },
  computed: {
    ...mapGetters(['isTeacher']),
    formattedSubevaluationStarted() {
      return this.localSubevaluation && this.localSubevaluation.subevaluation_started
        ? Subtask.formatDate(this.localSubevaluation.subevaluation_started)
        : '';
    },
    formattedSubevaluationEnded() {
      return this.localSubevaluation && this.localSubevaluation.subevaluation_ended
        ? Subtask.formatDate(this.localSubevaluation.subevaluation_ended)
        : '';
    },
    formatedSubmissionDate() {
      return (date) => Submission.formatDate(date);
    },
  },
  data() {
    return {
      localSubtask: { ...this.subtask },
      localDeadline: { ...this.deadline },
      localSubevaluation: this.subevaluation ? { ...this.subevaluation } : null,
      localSubmission: this.submission ? { ...this.submission } : null,
      logContent: null,
      resultContent: null,
      loading: false,
    };
  },
  mounted() {
    if (this.$refs.collapseElement) {
      this.$refs.collapseElement.addEventListener('shown.bs.collapse', this.handleAccordionShown);
    }
  },
  beforeUnmount() {
    if (this.$refs.collapseElement) {
      this.$refs.collapseElement.removeEventListener('shown.bs.collapse', this.handleAccordionShown);
    }
  },
  methods: {
    handleAccordionShown() {
      if (this.localSubevaluation) {
        if (this.isTeacher && this.localSubevaluation.teacher_log_path && !this.logContent) {
          this.fetchFileContent(this.localSubevaluation.teacher_log_path, 'log');
        }
        if (this.localSubevaluation.student_log_path && !this.resultContent) {
          this.fetchFileContent(this.localSubevaluation.student_log_path, 'result');
        }
      }
    },
    pushToSubtask() {
      this.$router.push(`/subtask/${this.subtask.id}`);
    },
    async downloadFile(filePath) {
      try {
        const response = await EvaluationController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filePath.split('/').pop());
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    },
    async fetchFileContent(filePath, type) {
      this.loading = true;
      try {
        const response = await EvaluationController.downloadFile(filePath);
        const content = await response.data.text();
        if (type === 'log') {
          this.logContent = content;
        } else if (type === 'result') {
          this.resultContent = content;
        }
      } catch (error) {
        console.error('Error fetching file content:', error);
      } finally {
        this.loading = false;
      }
    },
    // Download multiple files
    async downloadFiles(filePaths, zipName) {
      try {
        const response = await SubtaskController.downloadFiles(filePaths);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', zipName || 'files.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    formatDate(date) {
      return date ? Deadline.formatDateTime(date) : '';
    },
  },
};
</script>